import React, { type ReactNode, useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import {
	type Edge,
	attachClosestEdge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { type DropOperation, useOnDrop } from './use-on-drop';

const PLATFORM_ISSUE_TABLE_ROW = Symbol('PLATFORM_ISSUE_TABLE_ROW');

// TODO: use shared component from https://stash.atlassian.com/projects/JIRACLOUD/repos/jira-frontend/pull-requests/106327/diff#local-conf%2Ftsconfigs%2F%40atlassian-jira-software-drop-indicator%2Ftsconfig.app.json
// using <td> is highly questionable and due to the table layout / DOM nesting validation
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropIndicator = styled.td<{ edge: Edge }>({
	position: 'absolute',
	top: (props) => (props.edge === 'top' ? token('space.0', '0') : undefined),
	bottom: (props) => (props.edge === 'bottom' ? token('space.0', '0') : undefined),
	left: token('space.0', '0'),
	width: '100%',
	height: '2px',
	zIndex: 1,
	padding: token('space.0', '0'),
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	backgroundColor: token('color.icon.brand', colors.B300),
	pointerEvents: 'none',
	'&::before': {
		content: "''",
		position: 'absolute',
		height: 0,
		width: 0,
		top: token('space.negative.025', '-2px'),
		left: token('space.negative.050', '-4px'),
		borderTop: `${token('space.050', '4px')} solid transparent`,
		borderBottom: `${token('space.050', '4px')} solid transparent`,
		borderLeft: `${token('space.100', '8px')} solid ${token('color.border.brand', colors.B300)}`,
	},
});

interface UseDraggableTableRowParams {
	ref: React.RefObject<HTMLElement>;
	enableRowDragging: boolean;
	issueId?: string;
	issuesConnectionId: string;
}

type DropState = null | {
	edge: Edge;
};

interface UseDraggableTableRowResult {
	dropState: DropState;
	dropIndicator: ReactNode;
}

export const useDraggableTableRow = ({
	ref,
	enableRowDragging,
	issueId,
	issuesConnectionId,
}: UseDraggableTableRowParams): UseDraggableTableRowResult => {
	const onDrop = useOnDrop(issuesConnectionId);
	const [dropState, setDropState] = useState<DropState>(null);

	useEffect(() => {
		const tableRowElement = ref.current;

		if (!tableRowElement) {
			return;
		}
		if (!enableRowDragging) {
			return;
		}
		if (issueId == null) {
			return;
		}

		return combine(
			draggable({
				element: tableRowElement,
				getInitialData() {
					return {
						type: PLATFORM_ISSUE_TABLE_ROW,
						issueId,
					};
				},
			}),
			dropTargetForElements({
				element: tableRowElement,
				getData({ input, element }) {
					const data = {
						type: PLATFORM_ISSUE_TABLE_ROW,
						issueId,
					};
					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges: ['top', 'bottom'],
					});
				},
				canDrop(args) {
					return args.source.data.type === PLATFORM_ISSUE_TABLE_ROW;
				},
				onDragEnter(args) {
					if (args.source.data.type !== PLATFORM_ISSUE_TABLE_ROW) {
						return;
					}

					const edge = extractClosestEdge(args.self.data);
					if (!edge) {
						return;
					}
					setDropState({
						edge,
					});
				},
				onDragLeave() {
					setDropState(null);
				},
				onDrop(args) {
					if (args.source.data.type !== PLATFORM_ISSUE_TABLE_ROW) {
						return;
					}

					const edge = extractClosestEdge(args.self.data);
					const sourceIssueId = args.source.data.issueId;
					const targetIssueId = args.self.data.issueId;
					const dropOperation: DropOperation = {
						sourceIssueId: String(sourceIssueId),
						targetIssueId: String(targetIssueId),
						edge: edge === 'top' ? 'top' : 'bottom',
					};
					setDropState(null);
					onDrop(dropOperation);
				},
			}),
		);
	}, [enableRowDragging, issueId, onDrop, ref]);

	return {
		dropState,
		dropIndicator: dropState?.edge && <DropIndicator edge={dropState.edge} />,
	};
};
